import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, EnrollmentToken, IssuedEnrollmentToken } from '../lib/api';
import { absoluteTime, relativeTime } from '../lib/format';
import { ErrorState } from '../components/QueryState';

const TTL_OPTIONS: Array<{ label: string; value: number | null }> = [
  { label: '1 hour', value: 3600 },
  { label: '24 hours', value: 86400 },
  { label: '7 days', value: 7 * 86400 },
  { label: 'Never expires', value: null },
];

function installCommand(url: string): string {
  const insecure = url.startsWith('https://') ? '' : 'INSECURE=1 ';
  return ` curl ${url} | ${insecure}sudo bash`;
}

export function Enrollment() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['enrollment-tokens'],
    queryFn: () => api.get<{ tokens: EnrollmentToken[] }>('/enrollment-tokens/'),
  });

  const [singleUse, setSingleUse] = useState(true);
  const [ttl, setTtl] = useState<number | null>(86400);
  const [note, setNote] = useState('');
  const [allowRebind, setAllowRebind] = useState(false);
  const [issued, setIssued] = useState<IssuedEnrollmentToken | null>(null);

  const issue = useMutation({
    mutationFn: () =>
      api.post<IssuedEnrollmentToken>('/enrollment-tokens/', {
        single_use: singleUse,
        expires_in_seconds: ttl,
        note,
        allow_rebind: allowRebind,
      }),
    onSuccess: (r) => {
      setIssued(r);
      setNote('');
      setAllowRebind(false);
      qc.invalidateQueries({ queryKey: ['enrollment-tokens'] });
    },
  });

  const setRebind = useMutation({
    mutationFn: ({ id, allow }: { id: string; allow: boolean }) =>
      api.patch(`/enrollment-tokens/${id}`, { allow_rebind: allow }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['enrollment-tokens'] }),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/enrollment-tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['enrollment-tokens'] }),
  });

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Enrollment</h1>
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}

      <section className="card space-y-3">
        <h2 className="font-semibold">Issue enrollment token</h2>
        <p className="text-sm text-muted">
          An enrollment token is only valid for the bootstrap install. The script the server hands back
          contains a separate <em>operational token</em> bound to the new host's UUID, so a leaked install URL
          cannot read or write any existing client's data.
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={singleUse} onChange={(e) => setSingleUse(e.target.checked)} />
            Single-use (recommended)
          </label>
          <label
            className="text-sm flex items-center gap-2"
            title="Lets this token re-enroll a host whose machine-id is already enrolled, WITHOUT its current operational token. Use only for recovery (lost token, OS reinstall, cloned host)."
          >
            <input type="checkbox" checked={allowRebind} onChange={(e) => setAllowRebind(e.target.checked)} />
            Allow rebind
          </label>
          <label className="text-sm flex flex-col gap-1">
            <span className="text-muted">Lifetime</span>
            <select
              className="select"
              value={ttl === null ? 'null' : String(ttl)}
              onChange={(e) => setTtl(e.target.value === 'null' ? null : Number(e.target.value))}
            >
              {TTL_OPTIONS.map((o) => (
                <option key={String(o.value)} value={o.value === null ? 'null' : String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm flex flex-col gap-1 grow min-w-[220px]">
            <span className="text-muted">Note (e.g. "frankfurt prod web batch")</span>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
          </label>
          <button className="btn btn-primary" disabled={issue.isPending} onClick={() => issue.mutate()}>
            {issue.isPending ? 'Issuing…' : 'Issue'}
          </button>
        </div>

        {allowRebind && (
          <div className="text-xs text-danger">
            Rebind tokens can reclaim a host whose machine-id is already enrolled <em>without</em> proving possession of
            its current operational token — anyone holding the install URL could rotate that host's credentials. Issue
            with a short lifetime and single-use, and revoke once recovery is done.
          </div>
        )}

        {issued && (
          <div className="mt-3 p-3 border border-accent/40 rounded space-y-2 bg-accent/5">
            <div className="text-sm font-semibold">Token (copy now — will not be shown again):</div>
            <div className="font-mono text-xs break-all select-all">{issued.token}</div>
            <div className="text-xs text-muted">
              Install command — prefix with a leading space and run <code>export HISTCONTROL=ignorespace</code>{' '}
              once in your shell so the command stays out of <code>~/.bash_history</code>:
            </div>
            <pre className="font-mono text-xs whitespace-pre-wrap break-all bg-bg p-2 rounded border border-border select-all">
{installCommand(issued.install_url)}
            </pre>
            {!issued.install_url.startsWith('https://') && (
              <div className="text-xs text-danger">
                Server is on <code>http://</code>. Set up TLS (nginx + certbot) and update <code>PUBLIC_URL</code>{' '}
                so new install commands are HTTPS-only — <code>INSECURE=1</code> is only safe for local dev.
              </div>
            )}
            <button className="btn" onClick={() => setIssued(null)}>Dismiss</button>
          </div>
        )}
        {issue.isError && <div className="text-danger text-sm">{issue.error.message}</div>}
      </section>

      <section className="card overflow-auto">
        <h2 className="font-semibold mb-3">Active & recent enrollment tokens</h2>
        {revoke.isError && <div className="text-danger text-sm mb-2">{revoke.error.message}</div>}
        {setRebind.isError && <div className="text-danger text-sm mb-2">{setRebind.error.message}</div>}
        <table className="table">
          <thead>
            <tr>
              <th>Token</th>
              <th>Single-use</th>
              <th>Rebind</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Used by</th>
              <th>Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={9} className="text-muted">Loading…</td></tr>}
            {data?.tokens.length === 0 && (
              <tr><td colSpan={9} className="text-muted">No enrollment tokens. Issue one above to onboard your first host.</td></tr>
            )}
            {data?.tokens.map((t) => (
              <tr key={t.id}>
                <td className="font-mono text-xs">{t.masked}</td>
                <td>{t.single_use ? 'yes' : 'no'}</td>
                <td>
                  {t.allow_rebind && <span className="badge badge-blocked">rebind</span>}
                  {t.status === 'active' && (
                    <button
                      className="btn ml-1"
                      disabled={setRebind.isPending}
                      title={
                        t.allow_rebind
                          ? 'Disable rebind — require the operational token again'
                          : 'Allow this token to reclaim an already-enrolled machine-id without its operational token'
                      }
                      onClick={() => setRebind.mutate({ id: t.id, allow: !t.allow_rebind })}
                    >
                      {t.allow_rebind ? 'Disable' : 'Enable'}
                    </button>
                  )}
                </td>
                <td title={absoluteTime(t.created_at)}>{relativeTime(t.created_at)}</td>
                <td>
                  {t.expires_at === null ? <span className="text-muted">never</span> :
                    <span title={absoluteTime(t.expires_at)}>{relativeTime(t.expires_at)}</span>}
                </td>
                <td>
                  {t.status === 'active' && <span className="badge badge-ok">active</span>}
                  {t.status === 'used' && <span className="badge">used</span>}
                  {t.status === 'expired' && <span className="badge badge-blocked">expired</span>}
                </td>
                <td className="font-mono text-xs">{t.used_by_uuid ? t.used_by_uuid.slice(0, 8) + '…' : ''}</td>
                <td className="text-xs">{t.note}</td>
                <td>
                  <button
                    className="btn btn-danger"
                    disabled={t.status !== 'active'}
                    onClick={() => {
                      if (confirm('Revoke this enrollment token?')) revoke.mutate(t.id);
                    }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
